import { ErrorOutline } from "@mui/icons-material";
import { CircularProgress, Stack, styled, Typography } from "@mui/material";
import type { UseQueryResult } from "@tanstack/react-query";
import { Card } from "~/components/Card";
import { Center } from "~/components/Center";
import { Details } from "~/components/Details";
import {
  DetailsLayout,
  LockCard,
  RelatedResource,
} from "~/components/DetailsCards";
import { JsonField } from "~/components/DetailsCards/JsonField";
import {
  OBJECT_KEY_DELIMITER,
  ObjectSearchRequestProvider,
} from "~/components/ObjectExplorer";
import { QueryRenderer } from "~/components/QueryRenderer";
import { ErrorMessage } from "~/components/error-message";
import type { Record } from "~/lqs";
import {
  EditLqsResourceForm,
  LqsHistoryCard,
  LqsManageCard,
  LqsResourceFields,
  useDeleteRecord,
  useRecord,
  useUpdateRecord,
} from "~/lqs";
import { lqsRoutePaths, useLqsNavigator, useTypedParams } from "~/paths";
import { selectData } from "~/utils";
import { LogObjectTable } from "../../logs/components/LogObjectTable";
import { editRecordSchema } from "../schemas";

export function RecordDetails() {
  const { topicId, timestamp } = useTypedParams(
    lqsRoutePaths.RECORD_DETAILS,
    (params) => ({
      ...params,
      timestamp: BigInt(params.timestamp),
    }),
  );

  const lqsNavigator = useLqsNavigator({
    toRecordEdit: true,
    toRecordTable: true,
  });

  const query = useRecord(topicId, timestamp, { select: selectData });

  const generalSection = <GeneralSection query={query} />;
  const dataSection = <DataSection query={query} />;
  const relatedResourcesSection = (
    <Stack spacing={2}>
      <Typography variant="h4" component="h2">
        Related Resources
      </Typography>
      <RelatedObjects query={query} />
    </Stack>
  );
  const infoSection = <InfoSection query={query} />;
  const lockSection = (
    <LockCard
      resourceName="record"
      query={query}
      updateMutation={useUpdateRecord(topicId, timestamp)}
    />
  );
  const historySection = <LqsHistoryCard query={query} />;
  const manageSection = (
    <LqsManageCard
      resourceName="record"
      query={query}
      editLocation={lqsNavigator.toRecordEdit({ topicId, timestamp })}
      deleteMutation={useDeleteRecord(topicId, timestamp)}
      getReadableName={(record) => String(record.timestamp)}
      listLocation={lqsNavigator.toRecordTable({ topicId })}
    />
  );

  return (
    <DetailsLayout
      primaryGridColumn={
        <>
          {generalSection}
          {dataSection}
          {relatedResourcesSection}
        </>
      }
      secondaryGridColumn={
        <>
          {infoSection}
          {lockSection}
          {historySection}
          {manageSection}
        </>
      }
      stack={
        <>
          {generalSection}
          {infoSection}
          {dataSection}
          {lockSection}
          {historySection}
          {manageSection}
          {relatedResourcesSection}
        </>
      }
    />
  );
}

function GeneralSection({ query }: { query: UseQueryResult<Record> }) {
  return (
    <Card title="General">
      <LqsResourceFields
        registryKey="record"
        query={query}
        fields={[
          { dataType: "id", accessor: "timestamp" },
          {
            dataType: "foreign-key",
            resourceType: "topic",
            accessor: "topicId",
          },
          { dataType: "foreign-key", resourceType: "log", accessor: "logId" },
          {
            dataType: "foreign-key",
            resourceType: "ingestion",
            accessor: "ingestionId",
          },
          { dataType: "bytes", accessor: "dataOffset" },
          { dataType: "bytes", accessor: "dataLength" },
          { dataType: "text", accessor: "chunkCompression" },
          { dataType: "bytes", accessor: "chunkOffset" },
          { dataType: "bytes", accessor: "chunkLength" },
          { dataType: "text", accessor: "source" },
          { dataType: "json", accessor: "context" },
        ]}
      />
    </Card>
  );
}

const ErrorContent = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateAreas: `
    "icon message"
    "icon error"
  `,
  columnGap: theme.spacing(2),
  gridTemplateColumns: "auto minmax(0, 1fr)",
}));

function DataSection({ query }: { query: UseQueryResult<Record> }) {
  const hasError = query.data?.error != null;

  return (
    <Card title="Data" error={hasError}>
      {hasError ? (
        <ErrorContent>
          <ErrorOutline
            sx={{
              gridArea: "icon",
              fontSize: "3rem",
            }}
            color="error"
          />
          <Typography paragraph sx={{ gridArea: "message" }}>
            The record failed to process
          </Typography>
          <Details sx={{ gridArea: "error" }}>
            <Details.Summary>Expand to see error</Details.Summary>
            <JsonField value={query.data!.error} />
          </Details>
        </ErrorContent>
      ) : (
        <LqsResourceFields
          registryKey="record"
          query={query}
          fields={[{ dataType: "json", accessor: "queryData" }]}
        />
      )}
    </Card>
  );
}

function RelatedObjects({ query }: { query: UseQueryResult<Record> }) {
  const lqsNavigator = useLqsNavigator({ toLogObjectTable: true });

  return (
    <QueryRenderer
      query={query}
      loading={
        <Card>
          <Center sx={{ height: 350 }}>
            <CircularProgress />
          </Center>
        </Card>
      }
      error={
        <Card>
          <ErrorMessage>Couldn't load objects</ErrorMessage>
        </Card>
      }
      success={(record) => {
        const recordPrefix = [
          "topics",
          record.topicId,
          "records",
          record.timestamp,
          "",
        ].join(OBJECT_KEY_DELIMITER);

        return (
          <RelatedResource
            text="Objects"
            to={lqsNavigator.toLogObjectTable(
              { logId: record.logId },
              { directory: recordPrefix },
            )}
            table={
              <ObjectSearchRequestProvider embedded>
                <LogObjectTable
                  logId={record.logId}
                  homeName={String(record.timestamp)}
                  subResourcePrefix={recordPrefix}
                />
              </ObjectSearchRequestProvider>
            }
          />
        );
      }}
    />
  );
}

function InfoSection({ query }: { query: UseQueryResult<Record> }) {
  const { topicId, timestamp } = useTypedParams(
    lqsRoutePaths.RECORD_DETAILS,
    (params) => ({
      ...params,
      timestamp: BigInt(params.timestamp),
    }),
  );

  return (
    <EditLqsResourceForm
      resourceName="record"
      registryKey="record"
      disabled={(record) => record.locked}
      footer={(record) =>
        record.locked && (
          <Typography variant="body2">
            Unlock this record to make updates.
          </Typography>
        )
      }
      query={query}
      schema={editRecordSchema}
      descriptors={[{ name: "note", type: "text", multiline: true }]}
      mutation={useUpdateRecord(topicId, timestamp)}
    />
  );
}
